import { useCallback } from 'react'
import {
  DefaultEditorOptions,
  RichTextInputToolbar,
  ClearButtons,
  FormatButtons,
  LevelSelect,
  LinkButtons,
  ListButtons,
  AlignmentButtons,
  QuoteButtons,
  RichTextInputProps,
  RichTextInput as RaRichTextInput,
} from 'ra-input-rich-text'
import { styled, Box } from '@mui/material'
import { HorizontalRuleButton, ImageButtons, FontColorButton } from './ToolbarButtons'
import { useFormState } from 'react-hook-form'
import { CustomToolbarProps, TextEditorProps } from './type'
import RichTextInput from './RichTextInput'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import ImageTiptap from '@tiptap/extension-image'
import ColorTiptap from '@tiptap/extension-color'

const CustomToolbar: React.FC<CustomToolbarProps> = ({ size = 'medium' }) => {
  return (
    <RichTextInputToolbar>
      <LevelSelect size={size} />
      <FormatButtons size={size} />
      <FontColorButton size={size} />
      <AlignmentButtons size={size} />
      <ListButtons size={size} />
      <LinkButtons size={size} />
      <ImageButtons size={size} />
      <QuoteButtons size={size} />
      <ClearButtons size={size} />
      <HorizontalRuleButton size={size} />
    </RichTextInputToolbar>
  )
}

const EditorOptions = {
  ...DefaultEditorOptions,
  extensions: [...DefaultEditorOptions.extensions, HorizontalRule, ImageTiptap, ColorTiptap],
}

export const TextEditor: React.FC<TextEditorProps> = ({
  source,
  editorHeight = 200,
  fullWidth = false,
  ...props
}) => {
  const { errors } = useFormState()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const RichTextInputStyled = useCallback(
    styled(RichTextInput)(() => ({
      '& .RaRichTextInput-editorContent': {
        width: fullWidth ? '100%' : 'auto',
        '& .ProseMirror': {
          minHeight: editorHeight,
          maxHeight: '70vh',
          overflow: 'scroll',
        },
      },
    })),
    [editorHeight, errors[source]],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const EditorWrapper = useCallback(
    styled(Box)(() => ({
      width: fullWidth ? '100%' : 'auto',
    })),
    [fullWidth],
  )
  return (
    <EditorWrapper>
      <RichTextInputStyled
        className={errors[source] ? 'Mui-error' : ''}
        source={source}
        editorOptions={EditorOptions}
        toolbar={<CustomToolbar />}
        {...props}
      />
    </EditorWrapper>
  )
}
interface MyRichTextInputProps extends RichTextInputProps, CustomToolbarProps {}
const RaRichTextInputStyled = styled(RaRichTextInput)(() => ({
  '& .RaRichTextInput-editorContent': {
    '& .ProseMirror': {
      minHeight: 200,
      maxHeight: '70vh',
      overflow: 'scroll',
    },
  },
}))
export const MyRichTextInput = ({ size, ...props }: MyRichTextInputProps): JSX.Element => {
  return (
    <RaRichTextInputStyled
      editorOptions={EditorOptions}
      toolbar={<CustomToolbar size={size} />}
      {...props}
    />
  )
}
